import { FileTextIcon } from 'lucide-react'
import { useState } from 'react'

import { AnalysisQuote } from '@/gen/controls/v1/analysis_result_pb'

import { assertUnreachable } from '@/utils/type-utils'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Markdown } from '@/components/shared/markdown/markdown'
import { SourceDocumentViewer } from '@/components/shared/source-document-viewer/source-document-viewer'

import type { QuoteGroup } from './types'
import { analysisQuoteTitle, groupQuotes } from './utils'

type AnalysisQuotesProps = {
  quotes: AnalysisQuote[]
  readonly?: boolean
}

type AnalysisQuoteBodyProps = {
  quote: AnalysisQuote
  readonly: boolean
}

const AnalysisQuoteBody = ({ quote, readonly }: AnalysisQuoteBodyProps) => {
  switch (quote.quote.case) {
    case 'documentQuote':
      return <SourceDocumentViewer documentQuote={quote.quote.value} readonly={readonly} />
    case 'markdown':
      return (
        <Markdown firstTableColumnAsPlainText bordered allowRawHtml>
          {quote.quote.value}
        </Markdown>
      )
    case undefined:
      return null
    default:
      return assertUnreachable(quote.quote)
  }
}

/**
 * Renders every evidence item expanded under its title, for exports and other
 * print-style views.
 */
export const AnalysisQuotes = ({ quotes, readonly = false }: AnalysisQuotesProps) => {
  if (quotes.length === 0) return null

  return (
    <div className='space-y-4'>
      {quotes.map((quote, index) => (
        <div key={index}>
          <div className='mb-2 text-main-foreground-secondary text-label-default'>
            {analysisQuoteTitle(quote)}
          </div>
          <AnalysisQuoteBody quote={quote} readonly={readonly} />
        </div>
      ))}
    </div>
  )
}

type AnalysisQuoteListProps = AnalysisQuotesProps & {
  onOpen?: (quote: AnalysisQuote, index: number) => void
  autoOpenSingle?: boolean
  groupBy?: (quote: AnalysisQuote) => string | undefined
}

/**
 * Lists evidence as clickable titles; opening one shows its content below the
 * list, one item at a time, the way risk insights present evidence.
 */
export const AnalysisQuoteList = ({
  quotes,
  readonly = false,
  onOpen,
  autoOpenSingle = true,
  groupBy,
}: AnalysisQuoteListProps) => {
  const groups = groupQuotes(quotes, groupBy)
  // A lone item opens by itself; that isn't a user view, so onOpen doesn't fire.
  const [openKey, setOpenKey] = useState<string | null>(
    autoOpenSingle && groups.length === 1 ? groups[0].key : null,
  )
  if (groups.length === 0) return null

  // Single quote with auto-open on: skip the title list entirely and render the body on its own —
  // the clickable title would be redundant since there's nothing to pick between. When
  // autoOpenSingle is off, the caller wants the item collapsed by default, so keep the button.
  if (groups.length === 1 && autoOpenSingle) {
    return (
      <div className='flex flex-col gap-4'>
        {groups[0].quotes.map((quote, index) => (
          <AnalysisQuoteBody key={index} quote={quote} readonly={readonly} />
        ))}
      </div>
    )
  }

  const openGroup = groups.find((group) => group.key === openKey)

  const toggle = (group: QuoteGroup, index: number) => {
    const isOpening = openKey !== group.key
    setOpenKey(isOpening ? group.key : null)
    if (isOpening) onOpen?.(group.first, index)
  }

  return (
    <div>
      <div className='flex flex-col items-start gap-2'>
        {groups.map((group, index) => (
          <Button
            key={group.key}
            type='button'
            variant='link'
            className='inline-flex h-auto max-w-full items-start justify-start gap-2 space-x-0 !whitespace-normal px-0 py-0 text-left !text-main-foreground-accent text-label-default'
            onClick={() => toggle(group, index)}
            aria-expanded={openKey === group.key}
          >
            <FileTextIcon className='mt-0.5 size-4 shrink-0' aria-hidden />
            <span className='min-w-0 break-words'>{analysisQuoteTitle(group.first)}</span>
          </Button>
        ))}
      </div>
      {openGroup ? (
        <div className='mt-4 flex flex-col gap-4'>
          {openGroup.quotes.map((quote, index) => (
            <AnalysisQuoteBody key={index} quote={quote} readonly={readonly} />
          ))}
        </div>
      ) : null}
    </div>
  )
}
